// Shared local-storage helpers and badge rendering for the Sight-Reading Trainer,
// Daily Challenge and Badge Gallery. All personal data lives in the browser.

// ---------------------------------------------------------------------------
// Shareable personal-best badge (canvas -> PNG)
// ---------------------------------------------------------------------------
export function makeBadgeDataUrl({ difficulty, streak, accuracy, notes }) {
  const c = document.createElement("canvas");
  c.width = 1200;
  c.height = 630;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#121212";
  ctx.fillRect(0, 0, 1200, 630);
  ctx.fillStyle = "#b78b81";
  ctx.fillRect(0, 0, 1200, 12);
  ctx.textAlign = "left";
  ctx.fillStyle = "#c9a24a";
  ctx.font = "600 30px Georgia, serif";
  ctx.fillText("THE JT HUB · SIGHT-READING TRAINER", 80, 110);
  ctx.fillStyle = "#f2ede3";
  ctx.font = "600 84px Georgia, serif";
  ctx.fillText("New personal best", 80, 220);
  ctx.fillStyle = "#c9c2b6";
  ctx.font = "400 32px Georgia, serif";
  ctx.fillText(`${String(difficulty).toUpperCase()} LEVEL`, 80, 278);
  const stats = [["Best streak", streak], ["Accuracy", accuracy + "%"], ["Notes", notes]];
  stats.forEach(([label, val], i) => {
    const x = 80 + i * 360;
    ctx.fillStyle = "#b78b81";
    ctx.font = "600 96px Georgia, serif";
    ctx.fillText(String(val), x, 470);
    ctx.fillStyle = "#8f887c";
    ctx.font = "400 26px Georgia, serif";
    ctx.fillText(String(label).toUpperCase(), x, 520);
  });
  ctx.fillStyle = "#8f887c";
  ctx.font = "400 26px Georgia, serif";
  ctx.fillText("Learn to read music with Jason Thompson", 80, 590);
  return c.toDataURL("image/png");
}

export async function shareBadge(stats) {
  const url = makeBadgeDataUrl(stats);
  try {
    const blob = await (await fetch(url)).blob();
    const file = new File([blob], "jt-personal-best.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "My JT Hub personal best" });
      return;
    }
  } catch (e) {}
  const a = document.createElement("a");
  a.href = url;
  a.download = "jt-personal-best.png";
  a.click();
}

// ---------------------------------------------------------------------------
// Badge gallery (every personal-best a learner earns)
// ---------------------------------------------------------------------------
export function readBadges() {
  try {
    return JSON.parse(localStorage.getItem("jt_badges_gallery") || "[]") || [];
  } catch (e) {
    return [];
  }
}

export function saveBadge(badge) {
  const list = readBadges();
  const entry = { id: Date.now(), date: new Date().toISOString(), ...badge };
  list.unshift(entry);
  try {
    localStorage.setItem("jt_badges_gallery", JSON.stringify(list.slice(0, 60)));
  } catch (e) {}
  return entry;
}

// ---------------------------------------------------------------------------
// Per-note accuracy map (which notes a learner misses most), per difficulty+clef
// ---------------------------------------------------------------------------
export function readNoteStats() {
  try {
    return JSON.parse(localStorage.getItem("jt_note_stats") || "{}") || {};
  } catch (e) {
    return {};
  }
}

export function recordNote(difficulty, clef, pitch, correct) {
  const all = readNoteStats();
  const dk = all[difficulty] || {};
  const ck = dk[clef] || {};
  const cur = ck[pitch] || { attempts: 0, correct: 0 };
  cur.attempts += 1;
  if (correct) cur.correct += 1;
  ck[pitch] = cur;
  dk[clef] = ck;
  all[difficulty] = dk;
  try {
    localStorage.setItem("jt_note_stats", JSON.stringify(all));
  } catch (e) {}
  return all;
}

export function noteStatsFor(stats, difficulty, clef) {
  return (stats && stats[difficulty] && stats[difficulty][clef]) || {};
}

// ---------------------------------------------------------------------------
// Deterministic daily challenge (everyone gets the same notes for a given day)
// ---------------------------------------------------------------------------
const DAILY_POOL = {
  treble: ["C4", "D4", "E4", "F4", "G4", "A4", "B4", "C5", "D5", "E5", "F5"],
  bass: ["E2", "F2", "G2", "A2", "B2", "C3", "D3", "E3", "F3", "G3", "A3"],
};

function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function makeDailyChallenge(dateStr, count = 10) {
  const rng = mulberry32(hashStr("jt-daily-" + dateStr));
  const out = [];
  for (let i = 0; i < count; i++) {
    const clef = rng() < 0.5 ? "treble" : "bass";
    const pool = DAILY_POOL[clef];
    const pitch = pool[Math.floor(rng() * pool.length)];
    out.push({ clef, pitch });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Daily Challenge streak rewards (unlocked by consecutive-day streaks)
// ---------------------------------------------------------------------------
export const STREAK_TIERS = [
  { days: 3, label: "On a Roll", blurb: "3-day streak", color: "#b78b81" },
  { days: 7, label: "Week Warrior", blurb: "7-day streak", color: "#c9a24a" },
  { days: 14, label: "Fortnight Virtuoso", blurb: "14-day streak", color: "#7fa08a" },
  { days: 30, label: "Monthly Maestro", blurb: "30-day streak", color: "#9a7bb0" },
];

export function readStreakRewards() {
  try {
    return JSON.parse(localStorage.getItem("jt_streak_rewards") || "[]") || [];
  } catch (e) {
    return [];
  }
}

export function unlockStreakRewards(count) {
  const owned = readStreakRewards();
  const ownedDays = new Set(owned.map((r) => r.days));
  const newly = [];
  STREAK_TIERS.forEach((t) => {
    if (count >= t.days && !ownedDays.has(t.days)) {
      owned.push({ days: t.days, label: t.label, unlockedAt: new Date().toISOString() });
      newly.push(t);
    }
  });
  if (newly.length) {
    try {
      localStorage.setItem("jt_streak_rewards", JSON.stringify(owned));
    } catch (e) {}
  }
  return newly;
}

